import { create } from 'zustand';

export type NotificationKind = 'success' | 'error' | 'info' | 'warning';

export interface AppNotification {
	id: string;
	type: NotificationKind;
	title?: string;
	message: string;
	duration?: number | null; // ms; null means sticky
	createdAt: number;
}

interface NotificationStore {
	notifications: AppNotification[];
	push: (n: Omit<AppNotification, 'id' | 'createdAt'>) => string;
	dismiss: (id: string) => void;
	clearAll: () => void;
	// Shortcuts
	success: (message: string, opts?: Partial<Omit<AppNotification, 'id' | 'type' | 'message' | 'createdAt'>>) => string;
	error: (message: string, opts?: Partial<Omit<AppNotification, 'id' | 'type' | 'message' | 'createdAt'>>) => string;
	info: (message: string, opts?: Partial<Omit<AppNotification, 'id' | 'type' | 'message' | 'createdAt'>>) => string;
	warning: (message: string, opts?: Partial<Omit<AppNotification, 'id' | 'type' | 'message' | 'createdAt'>>) => string;
}

function uid() {
	return Math.random().toString(36).slice(2, 9);
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
	notifications: [],
	push: (n) => {
		const id = uid();
		const durationDefaults: Record<NotificationKind, number> = {
			success: 2500,
			error: 6000,
			info: 3000,
			warning: 4000,
		};
		const final: AppNotification = {
			id,
			type: (n.type as NotificationKind) ?? 'info',
			title: n.title,
			message: n.message,
			duration: n.duration === undefined ? durationDefaults[(n.type as NotificationKind) ?? 'info'] : n.duration,
			createdAt: Date.now(),
		};
		set((s) => ({ notifications: [final, ...s.notifications].slice(0, 8) }));
		return id;
	},
	dismiss: (id) => set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
	clearAll: () => set({ notifications: [] }),
	success: (message, opts) => get().push({ type: 'success', message, ...opts }),
	error: (message, opts) => get().push({ type: 'error', message, ...opts }),
	info: (message, opts) => get().push({ type: 'info', message, ...opts }),
	warning: (message, opts) => get().push({ type: 'warning', message, ...opts }),
})); 